/**
 * GlobalView — Earthquake Detail Popup
 *
 * Overlay showing earthquake details for the selected earthquake entity.
 * Shows magnitude (with color badge), location, depth, time, tsunami flag.
 * Draggable via header. Follows existing popup pattern (AircraftPopup).
 */

import { useEffect, useRef } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { magnitudeToColor } from '../../services/usgsEarthquakeClient';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import EntityBottomSheet from './EntityBottomSheet';
import styles from './EarthquakePopup.module.css';

// ─── Formatters ──────────────────────────────────────────────────

function timeAgo(isoStr: string): string {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  if (diff < 3600) return `vor ${Math.round(diff / 60)}min`;
  if (diff < 86400) return `vor ${(diff / 3600).toFixed(1)}h`;
  return `vor ${Math.round(diff / 86400)}d`;
}

function fmtTime(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
}

function fmtCoord(val: number, decimals = 4): string {
  return val.toFixed(decimals);
}

function magnitudeLabel(mag: number): string {
  if (mag < 2) return 'MIKRO';
  if (mag < 4) return 'LEICHT';
  if (mag < 5) return 'MODERAT';
  if (mag < 6) return 'STARK';
  if (mag < 7) return 'SCHWER';
  if (mag < 8) return 'GROSS';
  return 'KATASTROPHAL';
}

// ─── Shared Content ──────────────────────────────────────────────

interface EarthquakeContentProps {
  entity: {
    position: { lat: number; lon: number; alt: number };
    timestamp: string;
    properties: Record<string, unknown>;
  };
}

function EarthquakeContent({ entity }: EarthquakeContentProps) {
  const props = entity.properties;
  const mag = (props.magnitude as number) ?? 0;
  const magType = (props.magType as string) ?? '';
  const place = (props.place as string) ?? 'Unbekannt';
  const depth = props.depth as number | undefined;
  const tsunami = props.tsunami as number | undefined;
  const alert = props.alert as string | undefined;
  const significance = props.significance as number | undefined;
  const felt = props.felt as number | undefined;
  const mmi = props.mmi as number | undefined;
  const url = props.url as string | undefined;

  const magColor = magnitudeToColor(mag);

  return (
    <>
      {/* Magnitude badge */}
      <div className={styles.magBadge}>
        <span
          className={styles.magValue}
          style={{ color: magColor, textShadow: `0 0 12px ${magColor}` }}
        >
          {mag.toFixed(1)}
        </span>
        <div className={styles.magMeta}>
          <span className={styles.magLabel} style={{ color: magColor }}>{magnitudeLabel(mag)}</span>
          <span className={styles.magType}>{magType.toUpperCase()}</span>
        </div>
      </div>

      {/* Alert + Tsunami warnings */}
      {(tsunami === 1 || alert) && (
        <div className={styles.alerts}>
          {tsunami === 1 && (
            <span className={styles.tsunamiBadge}>🌊 TSUNAMI</span>
          )}
          {alert && (
            <span
              className={styles.alertBadge}
              style={{
                borderColor: alert === 'red' ? '#ff2222' :
                  alert === 'orange' ? '#ff8800' :
                  alert === 'yellow' ? '#ffcc00' : '#22cc88',
                color: alert === 'red' ? '#ff2222' :
                  alert === 'orange' ? '#ff8800' :
                  alert === 'yellow' ? '#ffcc00' : '#22cc88',
              }}
            >
              ALERT: {alert.toUpperCase()}
            </span>
          )}
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>ORT</div>
        <div className={styles.placeText}>{place}</div>
        <div className={styles.grid}>
          <Row label="LAT" value={fmtCoord(entity.position.lat)} suffix={entity.position.lat >= 0 ? '° N' : '° S'} />
          <Row label="LON" value={fmtCoord(entity.position.lon)} suffix={entity.position.lon >= 0 ? '° E' : '° W'} />
          {depth != null && <Row label="TIEFE" value={`${depth.toFixed(1)} km`} />}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>ZEIT</div>
        <div className={styles.grid}>
          <Row label="UTC" value={fmtTime(entity.timestamp)} />
          <Row label="AGO" value={timeAgo(entity.timestamp)} />
        </div>
      </div>

      {(significance != null || felt != null || mmi != null) && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>DATEN</div>
          <div className={styles.grid}>
            {significance != null && <Row label="SIG" value={`${significance}`} />}
            {felt != null && <Row label="FELT" value={`${felt} Berichte`} />}
            {mmi != null && <Row label="MMI" value={`${mmi.toFixed(1)}`} />}
          </div>
        </div>
      )}

      {url && entity.properties.source !== 'sed' && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.usgsLink}
        >
          🔗 USGS EVENT PAGE
        </a>
      )}
      {!url && (
        <a
          href="https://www.seismo.ethz.ch/en/earthquakes/switzerland/last-90-days/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.usgsLink}
        >
          🔗 SED EVENT PAGE
        </a>
      )}
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────

export default function EarthquakePopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  useEffect(() => {
    if (!selectedEntityId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEntity(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  if (!selectedEntityId || !entity || entity.type !== 'earthquake') return null;

  const props = entity.properties as Record<string, unknown>;
  const mag = (props.magnitude as number) ?? 0;
  const place = (props.place as string) ?? 'Erdbeben';

  const handleClose = () => setSelectedEntity(null);

  const contentEntity = {
    position: entity.position,
    timestamp: entity.timestamp,
    properties: props,
  };

  const desktopPopup = (
    <div
      className={styles.popup}
      style={dragStyle}
      id="earthquake-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>🔴</span>
          <span className={styles.headerTitle}>
            {place.length > 28 ? place.slice(0, 28) + '…' : place}
          </span>
        </div>
        <button className={styles.closeBtn} onClick={handleClose} title="Schliessen (ESC)" id="earthquake-popup-close">✕</button>
      </div>
      <EarthquakeContent entity={contentEntity} />
    </div>
  );

  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={`M${mag.toFixed(1)}`}
        entitySub={place}
        icon="🔴"
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        <EarthquakeContent entity={contentEntity} />
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value}{suffix && <span className={styles.suffix}>{suffix}</span>}
      </span>
    </div>
  );
}
